import { TryStyleClient } from '../client';
import { buildModal, injectStyles } from '../ui/createTryOnButton';
import type { TryOnButtonConfig, ModalOpts } from '../ui/createTryOnButton';

export interface WooCommerceSelectors {
  addToCart?: string;
  buyNow?: string;
  quantity?: string;
}

export interface WooCommerceWrapperConfig extends Omit<TryOnButtonConfig, 'retailerId' | 'firebaseConfig'> {
  /** Public backend endpoint */
  apiUrl: string;

  /** Optional platform identifier */
  platformKey?: string;

  /** Optional site origin override */
  origin?: string;

  /** Optional selector overrides */
  selectors?: WooCommerceSelectors;

  /** Metrics enabled by default */
  trackMetrics?: boolean;
}

const DEFAULT_SELECTORS: Required<WooCommerceSelectors> = {
  addToCart: 'button.single_add_to_cart_button',
  buyNow: '.woocommerce-buy-now-button, button[name="woocommerce_checkout_place_order"]',
  quantity: 'input[name="quantity"]',
};

const ATTRIBUTION_WINDOW_MS = 30 * 60 * 1000;

function readQty(selector: string): number {
  const el = document.querySelector<HTMLInputElement>(selector);
  if (!el) return 1;

  const n = parseInt(el.value, 10);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

function highlightWcSize(
  sizeRec: { upper?: string | null; lower?: string | null },
  category?: string,
): void {
  const cat = (category ?? '').toLowerCase();

  const isBottom = /^(bottom|pants|jeans|trousers|skirt|shorts|lower|leggings|joggers)/.test(cat);

  const recommendation = isBottom ? sizeRec.lower : sizeRec.upper;

  if (!recommendation) return;

  const candidates = [
    recommendation,
    recommendation.toUpperCase(),
    recommendation.toLowerCase(),
  ];

  for (const value of candidates) {
    const radio = document.querySelector<HTMLInputElement>(
      `input[name="attribute_pa_size"][value="${CSS.escape(value)}"]`,
    );

    if (radio) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const select = document.querySelector<HTMLSelectElement>(
      'select[name="attribute_pa_size"]',
    );

    if (select?.querySelector(`option[value="${CSS.escape(value)}"]`)) {
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}

const logoInline = `
  <span style="display:inline-flex;align-items:center;gap:2px;font-size:10px;font-weight:700">
    <span style="color:#80cbe6">Try</span>
    <span style="color:#1a1a1a">Style</span>
  </span>
`;

export function createWooCommerceWrapper(config: WooCommerceWrapperConfig): void {
  injectStyles();

  const {
    container,
    label = 'Try This On',
    apiUrl,
    platformKey,
    origin,
    productId,
    productName,
    productImage,
    category,
    selectors: selectorOverrides,
    trackMetrics = true,
    onResult: userOnResult,
    showBranding = false,
    ...sdkConfig
  } = config;

  if (!container) {
    console.error('[TryStyle] Missing container');
    return;
  }

  if (container.dataset.trystyleMounted === 'true') {
    return;
  }

  container.dataset.trystyleMounted = 'true';

  const selectors: Required<WooCommerceSelectors> = {
    ...DEFAULT_SELECTORS,
    ...selectorOverrides,
  };

  const wrapper = document.createElement('div');
  wrapper.style.cssText = 'display:inline-flex;flex-direction:column;align-items:center';

  const btnWrap = document.createElement('div');
  btnWrap.className = 'ts-tryon-btn-wrap';

  const button = document.createElement('button');
  button.className = 'ts-tryon-btn';
  button.setAttribute('aria-haspopup', 'dialog');
  button.innerHTML = `<div class="ts-tryon-btn-bg"></div><div class="ts-tryon-btn-border"></div><span>${label}</span>`;

  const powered = showBranding ? document.createElement('div') : null;
  if (powered) {
    powered.className = 'ts-powered';
    powered.innerHTML = `Powered by ${logoInline}`;
  }

  btnWrap.appendChild(button);
  wrapper.appendChild(btnWrap);
  if (powered) wrapper.appendChild(powered);
  container.appendChild(wrapper);

  const client = new TryStyleClient({
    apiUrl,
    ...sdkConfig,
  });

  let ready = false;
  let initFailed = false;
  let lastTryonAt: number | null = null;

  let resolvedRetailerId = '';
  let resolvedFirebaseConfig: ModalOpts['firebaseConfig'] = undefined;

  const modalOpts: ModalOpts = {
    retailerId: '',
    firebaseConfig: undefined,
    productId,
    productName,
    productImage,
    category,
    showBranding,
    onResult: (resultUrl, sizeRec) => {
      lastTryonAt = Date.now();

      if (sizeRec) {
        highlightWcSize(sizeRec, modalOpts.category);
      }

      userOnResult?.(resultUrl, sizeRec);
    },
  };

  function hasDoneTryon(): boolean {
    return lastTryonAt !== null && Date.now() - lastTryonAt < ATTRIBUTION_WINDOW_MS;
  }

  async function fetchPlatformConfig(): Promise<void> {
    const platformToken = client.platform.getCurrent();

    if (!platformToken) {
      throw new Error('Platform token not available after PKCE init');
    }

    const response = await fetch(`${apiUrl}/api/v1/public/platform/sdk/config`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Platform-Token': platformToken,
      },
      body: JSON.stringify({
        origin: origin || window.location.origin,
        platformKey,
      }),
    });

    const json = await response.json();

    if (!response.ok || !json?.success || !json?.data?.config) {
      throw new Error('Invalid config response');
    }

    const platformConfig = json.data.config;

    resolvedRetailerId = platformConfig.retailerId;
    resolvedFirebaseConfig = platformConfig.firebaseConfig;

    modalOpts.retailerId = resolvedRetailerId;
    modalOpts.firebaseConfig = resolvedFirebaseConfig;
  }

  client
    .init()
    .then(async () => {
      await fetchPlatformConfig();

      // If retailerId is missing after config fetch, the client is not registered
      // or setup is incomplete — treat as init failure and disable the button.
      if (!resolvedRetailerId) {
        throw new Error('Client not registered');
      }

      ready = true;
    })
    .catch((error: Error) => {
      console.error('[TryStyle] Initialization failed:', error.message);

      initFailed = true;
      btnWrap.classList.add('ts-disabled');
      button.classList.add('ts-disabled');

      if (!wrapper.querySelector('.ts-init-error')) {
        const errorNode = document.createElement('div');
        errorNode.className = 'ts-init-error';
        errorNode.textContent = 'Contact support to enable TryStyle on your site';
        wrapper.appendChild(errorNode);
      }
    });

  button.addEventListener('click', () => {
    if (initFailed) return; // paired with .ts-disabled pointer-events:none in CSS

    if (!ready) {
      console.warn('[TryStyle] SDK not ready yet');
      return;
    }

    if (document.querySelector('.ts-overlay')) return;

    buildModal(modalOpts, client);
  });

  const jq = (window as any).jQuery;

  if (typeof jq === 'function') {
    const form = jq('form.variations_form');

    const baseProductId = productId;
    const baseProductImage = productImage;

    form.on('show_variation', (_e: Event, variation: any) => {
      if (variation?.variation_id) {
        modalOpts.productId = String(variation.variation_id);
      }

      if (variation?.image?.src) {
        modalOpts.productImage = variation.image.src;
      }
    });

    form.on('reset_data', () => {
      modalOpts.productId = baseProductId;
      modalOpts.productImage = baseProductImage;
    });
  }

  if (!trackMetrics) return;

  function trackAddToCart(): void {
    if (!resolvedRetailerId) return;

    void client.metrics.recordAddToCart({
      retailerId: resolvedRetailerId,
      productId: String(modalOpts.productId),
      productName,
      quantity: readQty(selectors.quantity),
      source: hasDoneTryon() ? 'tryon' : 'organic',
      metadata: {
        platform: 'woocommerce',
        pageUrl: window.location.href,
      },
    });
  }

  function trackBuyNow(): void {
    if (!resolvedRetailerId) return;

    void client.metrics.recordClickThrough({
      retailerId: resolvedRetailerId,
      productId: String(modalOpts.productId),
      productName,
      redirectUrl: window.location.href,
      source: hasDoneTryon() ? 'tryon' : 'organic',
      metadata: {
        platform: 'woocommerce',
        pageUrl: window.location.href,
        trigger: 'buy_now',
      },
    });
  }

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target as Element;

      if (selectors.addToCart && target.closest(selectors.addToCart)) {
        trackAddToCart();
      } else if (selectors.buyNow && target.closest(selectors.buyNow)) {
        trackBuyNow();
      }
    },
    { capture: true },
  );

  if (typeof jq === 'function') {
    jq(document).on('added_to_cart', () => {
      trackAddToCart();
    });
  }
}
